import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import OnboardingLayout from '../../components/OnboardingLayout'
import OnboardingField from '../../components/onboarding/OnboardingField'
import { WEEKDAY_KEYS, weekdayLabel } from '../../lib/i18nCatalog'
import { invalidFieldsFromChecks, scrollToFirstFieldError } from '../../lib/onboarding/fieldValidation'
import { ONBOARDING, onboardingChipClass, onboardingFieldControlClass } from '../../lib/onboarding/formStyles'
import { ONBOARDING_STEP_COUNT } from '../../lib/onboarding'
import { normalizeTimeHHMM } from '../../lib/trainingSchedule'

import type { OnboardingStepProps } from './stepProps'

const WORKOUT_TIME_PRESETS = ['06:00', '07:00', '12:00', '17:00', '18:30'] as const

const Step2 = ({ onNext, onPrevious, initialData, stepIndex }: OnboardingStepProps) => {
  const { t } = useTranslation()
  const [formData, setFormData] = useState({
    weekly_commitment: (initialData?.weekly_commitment as string) || '',
    desk_bound: (initialData?.desk_bound as string) || '',
    meal_prep_experience: (initialData?.meal_prep_experience as string) || '',
    meal_prep_day: (initialData?.meal_prep_day as string) || '',
    rest_days: (initialData?.rest_days as string[]) || [],
    preferred_workout_start: initialData?.preferred_workout_start
      ? normalizeTimeHHMM(String(initialData.preferred_workout_start))
      : '',
  })
  const [error, setError] = useState('')
  const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set())

  const handleSubmit = () => {
    setError('')

    const checks = {
      weekly_commitment: Boolean(formData.weekly_commitment),
      desk_bound: formData.desk_bound !== '',
      meal_prep_experience: Boolean(formData.meal_prep_experience),
      meal_prep_day: Boolean(formData.meal_prep_day),
      rest_days: formData.rest_days.length > 0,
      preferred_workout_start: Boolean(formData.preferred_workout_start),
    }
    const invalid = invalidFieldsFromChecks(checks)
    if (invalid.size > 0) {
      setInvalidFields(invalid)
      setError(t('onboarding.logistics.fillAll'))
      scrollToFirstFieldError([...invalid])
      return
    }

    setInvalidFields(new Set())
    onNext({
      ...formData,
      weekly_commitment: parseInt(formData.weekly_commitment),
      desk_bound: formData.desk_bound === 'true',
    })
  }

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
    setInvalidFields((prev) => {
      if (!prev.has(name)) return prev
      const next = new Set(prev)
      next.delete(name)
      return next
    })
  }

  const toggleRestDay = (day: string) => {
    setFormData((prev) => ({
      ...prev,
      rest_days: prev.rest_days.includes(day) ? prev.rest_days.filter((d) => d !== day) : [...prev.rest_days, day],
    }))
    setInvalidFields((prev) => {
      if (!prev.has('rest_days')) return prev
      const next = new Set(prev)
      next.delete('rest_days')
      return next
    })
  }

  return (
    <OnboardingLayout step={stepIndex} totalSteps={ONBOARDING_STEP_COUNT} onNext={handleSubmit} onPrev={onPrevious}>
      <form className="space-y-6">
        <div>
          <h2 className={ONBOARDING.heading}>{t('onboarding.logistics.heading')}</h2>
          <p className={ONBOARDING.subtitle}>{t('onboarding.logistics.subtitle')}</p>
        </div>

        <OnboardingField id="weekly_commitment" label={t('onboarding.logistics.weeklyCommitment')} required invalid={invalidFields.has('weekly_commitment')}>
          <select
            name="weekly_commitment"
            value={formData.weekly_commitment}
            onChange={handleChange}
            className={onboardingFieldControlClass(ONBOARDING.select, invalidFields.has('weekly_commitment'))}
          >
            <option value="">{t('onboarding.form.select')}</option>
            <option value="3">{t('onboarding.logistics.days3')}</option>
            <option value="4">{t('onboarding.logistics.days4')}</option>
            <option value="5">{t('onboarding.logistics.days5')}</option>
          </select>
        </OnboardingField>

        <OnboardingField id="desk_bound" label={t('onboarding.logistics.deskBound')} required invalid={invalidFields.has('desk_bound')}>
          <select
            name="desk_bound"
            value={formData.desk_bound}
            onChange={handleChange}
            className={onboardingFieldControlClass(ONBOARDING.select, invalidFields.has('desk_bound'))}
          >
            <option value="">{t('onboarding.form.select')}</option>
            <option value="true">{t('onboarding.form.yes')}</option>
            <option value="false">{t('onboarding.form.no')}</option>
          </select>
        </OnboardingField>

        <OnboardingField id="meal_prep_experience" label={t('onboarding.logistics.mealPrepExp')} required invalid={invalidFields.has('meal_prep_experience')}>
          <select
            name="meal_prep_experience"
            value={formData.meal_prep_experience}
            onChange={handleChange}
            className={onboardingFieldControlClass(ONBOARDING.select, invalidFields.has('meal_prep_experience'))}
          >
            <option value="">{t('onboarding.form.select')}</option>
            <option value="never">{t('onboarding.logistics.prepNever')}</option>
            <option value="occasional">{t('onboarding.logistics.prepOccasional')}</option>
            <option value="weekly">{t('onboarding.logistics.prepWeekly')}</option>
            <option value="lifestyle">{t('onboarding.logistics.prepLifestyle')}</option>
          </select>
        </OnboardingField>

        <OnboardingField id="meal_prep_day" label={t('onboarding.logistics.mealPrepDay')} required invalid={invalidFields.has('meal_prep_day')}>
          <select
            name="meal_prep_day"
            value={formData.meal_prep_day}
            onChange={handleChange}
            className={onboardingFieldControlClass(ONBOARDING.select, invalidFields.has('meal_prep_day'))}
          >
            <option value="">{t('onboarding.form.select')}</option>
            {WEEKDAY_KEYS.map((day) => (
              <option key={day} value={day}>
                {weekdayLabel(t, day)}
              </option>
            ))}
          </select>
        </OnboardingField>

        <OnboardingField id="rest_days" label={t('onboarding.logistics.restDays')} required invalid={invalidFields.has('rest_days')}>
          <div className="grid gap-2 sm:grid-cols-2">
            {WEEKDAY_KEYS.map((day) => (
              <button
                key={day}
                type="button"
                onClick={() => toggleRestDay(day)}
                className={onboardingChipClass(formData.rest_days.includes(day))}
              >
                {weekdayLabel(t, day)}
              </button>
            ))}
          </div>
        </OnboardingField>

        <OnboardingField id="preferred_workout_start" label={t('onboarding.logistics.workoutTime')} required invalid={invalidFields.has('preferred_workout_start')}>
          <p className={`text-sm ${ONBOARDING.muted}`}>{t('onboarding.logistics.workoutTimeHint')}</p>
          <div className="grid gap-2 sm:grid-cols-2">
            {WORKOUT_TIME_PRESETS.map((time) => (
              <button
                key={time}
                type="button"
                onClick={() => {
                  setFormData({ ...formData, preferred_workout_start: time })
                  setInvalidFields((prev) => {
                    if (!prev.has('preferred_workout_start')) return prev
                    const next = new Set(prev)
                    next.delete('preferred_workout_start')
                    return next
                  })
                }}
                className={onboardingChipClass(formData.preferred_workout_start === time)}
              >
                {t(`onboarding.logistics.workoutTimePreset.${time.replace(':', '')}`)}
              </button>
            ))}
          </div>
          <label className="block space-y-2">
            <span className={`text-sm ${ONBOARDING.muted}`}>{t('onboarding.logistics.workoutTimeCustom')}</span>
            <input
              type="time"
              value={formData.preferred_workout_start}
              onChange={(event) => {
                setFormData({ ...formData, preferred_workout_start: event.target.value })
                setInvalidFields((prev) => {
                  if (!prev.has('preferred_workout_start')) return prev
                  const next = new Set(prev)
                  next.delete('preferred_workout_start')
                  return next
                })
              }}
              className={onboardingFieldControlClass(ONBOARDING.select, invalidFields.has('preferred_workout_start'))}
            />
          </label>
        </OnboardingField>

        {error ? <div className={ONBOARDING.error}>{error}</div> : null}
      </form>
    </OnboardingLayout>
  )
}

export default Step2
